import React from "react";
import { Settings } from "luxon";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import { QuestionListContainer } from "../../../containers/page/QuestionListContainer";
import { NavigationContainer } from "../../../containers/page/NavigationContainer";
import { NavigationType } from "../../../models/enums/NavigationType";
import Progress from "../../page/Progress";
import { ThemeModel } from "../../../models/ThemeModel";
import { Signal } from "../../../signals";
import { QuestionnaireModel } from "../../../models/QuestionnaireModel";
import { ValidationOptionsModel } from "../../../models/ValidationOptionsModel";
import { ValidationResultModel } from "../../../models/ValidationResultModel";
import ValidationSummary from "./ValidationSummaryRenderer";
import useStyles from "./PageRenderer.styles";
import useTranslatable from "../../../hooks/useTranslatable";
import { PageWrapperDimensionsContext } from "../../../hooks/useDimensionContext";
import TopNavigation from "../../page/TopNavigation";
import { useElementDimensions, DimensionModel } from "../../../hooks/useElementDimensions";
import ErrorRenderer from "../../shared/ErrorRenderer";
import { isIE } from "../../../utils";
import { StoreState } from "../../../models/states/StoreState";
import { useThunkDispatch } from "../../../redux/thunks";
import { setEnableLegacyBrowserWarning } from "../../../redux/actions";
import { translate } from "../../../models/utilities/translationUtilities";
import { isEmpty } from "../../../utils/lodash";
import { QuestionModel } from "../../../models/QuestionModel";

interface Props {
    questions: QuestionModel[];
    message: string | Error;
    language: string;
    questionnaire: QuestionnaireModel;
    matrixIndex: number;
    isNavigationDisabled: boolean;
    progress: number;
    title: string;
    theme: ThemeModel;
    validateSignal: Signal<ValidationOptionsModel, ValidationResultModel>;
    isValid: boolean;
    onNavigation(type: NavigationType): void;
    isDesktopPreviewView?: boolean;
}
interface PageProps {
    children?: React.ReactNode;
    pageRef: React.RefObject<HTMLDivElement>;
}
interface PageWrapperProps {
    children?: React.ReactNode;
    wrapperRef: React.Ref<HTMLDivElement>;
    dimension: DimensionModel;
}
interface PageContentProps {
    pageRef: React.RefObject<HTMLDivElement>;
    wrapperRef: React.Ref<HTMLDivElement>;
    dimension: DimensionModel;
}

function Page(props: PageProps) {
    const theme = useTheme();
    const classes = useStyles({ theme });
    const { children, pageRef } = props;

    return (
        <div role="none" className={classes.page} ref={pageRef}>
            <Skiplink />
            {children}
        </div>
    );
}

function PageWrapper(props: PageWrapperProps) {
    const theme = useTheme();
    const classes = useStyles({ theme });
    const { children, wrapperRef, dimension } = props;
    return (
        <div
            ref={wrapperRef}
            className={classnames(
                classes.pageWrapper,
                dimension && dimension.maxWidth(778) && classes.dynamicPageWrapper,
            )}
        >
            {children}
        </div>
    );
}

function ErrorMessage({ message }) {
    const theme = useTheme();
    const classes = useStyles({ theme });
    return process.env.NODE_ENV === "development" ? (
        <ErrorRenderer error={message} />
    ) : (
        <div className={classes.pageMessage}>An error has occurred.</div>
    );
}

function PageMessage({ message, pageRef, wrapperRef, dimension }) {
    const theme = useTheme();
    const classes = useStyles({ theme });
    return (
        <Page pageRef={pageRef}>
            <PageWrapper wrapperRef={wrapperRef} dimension={dimension}>
                <div className={classes.pageContent}>
                    {typeof message === "object" ? (
                        <ErrorMessage message={message} />
                    ) : (
                        <div
                            className={classes.pageMessage}
                            dangerouslySetInnerHTML={{ __html: message }}
                        />
                    )}
                </div>
            </PageWrapper>
        </Page>
    );
}

function Logo(props: Props) {
    const { theme: surveyTheme } = props;
    const theme = useTheme();
    const classes = useStyles({ theme });
    const logo = useTranslatable(surveyTheme, "Logo");
    const { LogoDescription } = theme as any;

    if (!logo) {
        return null;
    }
    return <img className={classes.pageHeaderLogo} src={logo} alt={LogoDescription ?? ""} />;
}

function PageHeader(props: Props) {
    const { theme: surveyTheme } = props;
    const theme = useTheme();
    const classes = useStyles({ theme });
    const pageHeader = useTranslatable(surveyTheme, "PageHeader");
    return (
        <>
            <header className={classes.pageHeader}>
                <Logo {...props} />
                <div
                    id="PageHeader"
                    className={classes.pageHeaderContent}
                    dangerouslySetInnerHTML={{ __html: pageHeader }}
                />
            </header>
            <div className="clear" />
        </>
    );
}

function PageFooter(props: Props) {
    const { theme: surveyTheme } = props;
    const theme = useTheme();
    const classes = useStyles({ theme });
    const pageFooter = useTranslatable(surveyTheme, "PageFooter");
    return (
        <>
            <footer
                className={classes.pageFooter}
                dangerouslySetInnerHTML={{ __html: pageFooter }}
            />
            <div className="clear" />
        </>
    );
}

function LegacyBrowserWarning({ isDisplayWide, questionnaire, language }) {
    const dispatch = useThunkDispatch();
    const theme: any = useTheme();
    const classes = useStyles({ theme });

    const buttonText = translate(questionnaire.theme, "acknowledgementButton", language);
    const message = translate(questionnaire, "legacyBrowserWarning", language);

    // Split message into sentences by replacing punctuation marks (optionally followed by spaces) with pipe and splitting by pipe
    const sentences = message.replace(/([.?!])\s*(?=[A-Z0-9])/g, "$1|").split("|");
    const firstSentence = sentences[0];

    // Join remaining sentences if any
    const remainingSentences = sentences.length > 1 ? sentences.slice(1).join(" ") : null;

    const onClick = () => {
        // Change questionnaire setting to hide warning
        dispatch(setEnableLegacyBrowserWarning(false));
    };

    return (
        <div
            className={
                isDisplayWide
                    ? classes.legacyBrowserWarningFloating
                    : classes.legacyBrowserWarningInline
            }
        >
            <div className={classes.legacyBrowserWarningIconContainer}>
                <div className={classes.legacyBrowserWarningIconBackground}>
                    <FontAwesomeIcon
                        size="3x"
                        icon={["fas", "exclamation"]}
                        transform="shrink-7"
                        color={theme.ControlBackground}
                    />
                </div>
            </div>
            <div className={classes.legacyBrowserWarningContent}>
                <div>
                    <p className={classes.legacyBrowserWarningEmphasis}>{firstSentence}</p>
                    {remainingSentences && <p>{remainingSentences}</p>}
                </div>
                <div
                    className={classes.legacyBrowserWarningButton}
                    role="button"
                    onClick={onClick}
                    tabIndex={0}
                    onKeyPress={null}
                >
                    {buttonText}
                </div>
            </div>
        </div>
    );
}

function Backlink() {
    const theme: any = useTheme();
    const classes = useStyles({ theme });

    return (
        <div className={classes.backlinkContainer}>
            <a
                className={classes.backlink}
                href="https://www.voxco.com"
                target="_blank"
                rel="noopener noreferrer"
            >
                Survey Software
            </a>{" "}
            Powered by Voxco
        </div>
    );
}

function Skiplink() {
    const theme = useTheme();
    const classes = useStyles({ theme });

    return (
        <div className={classes.skiplinkContainer}>
            <a href="#maincontent">Skip to main content</a>
        </div>
    );
}

function PageContent(props: Props & PageContentProps) {
    const {
        pageRef,
        wrapperRef,
        dimension,
        title,
        validateSignal,
        onNavigation,
        theme: surveyTheme,
        questions,
        language,
        questionnaire,
        matrixIndex,
        isValid,
        progress,
        isDesktopPreviewView,
    } = props;

    const isDisplayWide = dimension.minWidth(901);
    const isLegacyBrowser = isIE();
    const enableLegacyBrowserWarning = useSelector(
        (state: StoreState) => state.interview.questionnaire.settings.ENABLE_LEGACY_BROWSER_WARNING,
    );

    const showLegacyBrowserWarning = enableLegacyBrowserWarning && isLegacyBrowser;

    Settings.defaultLocale = language;

    const theme: any = useTheme();
    const {
        ProgressBarType,
        ProgressBarDisplay,
        ProgressBarVisible,
        LanguageSelectorVisible,
        ViewResponsesButtonVisible,
        TopNavigationPosition,
        BottomNavigationPosition,
    } = theme;
    const topNavigationVisible = LanguageSelectorVisible || ViewResponsesButtonVisible;

    if (title) {
        document.title = title;
    }

    const showProgress = progress >= 0 && ProgressBarVisible;
    const showProgressTop =
        showProgress && (ProgressBarDisplay === "topAndBottom" || ProgressBarDisplay === "top");
    const showProgressBottom =
        showProgress && (ProgressBarDisplay === "topAndBottom" || ProgressBarDisplay === "bottom");

    const isProgressFloating = ProgressBarType === "floating";

    // Enable backlink for newly created surveys that don't yet have SettingsJson
    let enableBacklink = true;
    if (!isEmpty(surveyTheme.settings)) {
        enableBacklink = surveyTheme.settings.EnableBacklink;
    }

    return (
        <PageWrapperDimensionsContext.Provider value={dimension}>
            <Page pageRef={pageRef}>
                {showLegacyBrowserWarning && isDisplayWide && (
                    <LegacyBrowserWarning
                        isDisplayWide={isDisplayWide}
                        questionnaire={questionnaire}
                        language={language}
                    />
                )}
                {topNavigationVisible && TopNavigationPosition === "floating" && (
                    <TopNavigation theme={surveyTheme} />
                )}
                <PageWrapper wrapperRef={wrapperRef} dimension={dimension}>
                    {showLegacyBrowserWarning && !isDisplayWide && (
                        <LegacyBrowserWarning
                            isDisplayWide={isDisplayWide}
                            questionnaire={questionnaire}
                            language={language}
                        />
                    )}
                    {topNavigationVisible && TopNavigationPosition === "wrapper" && (
                        <TopNavigation theme={surveyTheme} />
                    )}
                    <PageHeader {...props} />
                    <ValidationSummary
                        language={language}
                        questionnaire={questionnaire}
                        isValid={isValid}
                    />
                    {showProgressTop && <Progress value={progress} position="top" />}
                    {(!showProgress || !showProgressTop || isProgressFloating) && <hr />}
                    {BottomNavigationPosition === "wrapper" && (
                        <NavigationContainer position="top" onNavigation={onNavigation} />
                    )}
                    <QuestionListContainer
                        questions={questions}
                        questionnaire={questionnaire}
                        matrixIndex={matrixIndex}
                        language={language}
                        validateSignal={validateSignal}
                        onNavigation={onNavigation}
                        theme={surveyTheme}
                        isDesktopPreviewView={isDesktopPreviewView}
                    />
                    <hr />
                    {BottomNavigationPosition === "wrapper" && (
                        <NavigationContainer position="bottom" onNavigation={onNavigation} />
                    )}
                    {showProgressBottom && <Progress value={progress} position="bottom" />}
                    <PageFooter {...props} />
                </PageWrapper>
                {BottomNavigationPosition === "floating" && (
                    <NavigationContainer position="bottom" onNavigation={onNavigation} />
                )}
                {enableBacklink && <Backlink />}
            </Page>
        </PageWrapperDimensionsContext.Provider>
    );
}

export default function PageRenderer(props: Props) {
    const { message } = props;
    const pageRef = React.useRef(null);
    const { ref: wrapperRef, dimensions: dimension } = useElementDimensions<HTMLDivElement>();

    if (message) {
        return <PageMessage {...{ ...props, pageRef, wrapperRef, dimension }} />;
    }

    return <PageContent {...{ ...props, pageRef, wrapperRef, dimension }} />;
}
